import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Collapse,
  Chip,
  Checkbox,
} from '@mui/material';
import {
  School as SchoolIcon,
  Class as ClassIcon,
  ExpandMore,
  ExpandLess,
  Add as AddIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard } from '../../components/common';
import { SchoolFormDialog, ClassFormDialog, getAssignmentTypeInfo } from '../../components/dialogs';
import { useFamilyData, useFamilyMembersData } from '../../hooks';
import { useFamily } from '../../context';
import { colors } from '../../theme';
import { ClassAssignment } from '../../types';

const SchoolPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data hooks - Schools are stored as contacts with category='school'
  const { items: allContacts, loading: contactsLoading, insert: insertContact, update: updateContact, remove: removeContact } = useFamilyData('contact');
  const { memberList } = useFamily();
  const memberIds = React.useMemo(() => memberList.map((m) => m.id), [memberList]);
  const { items: classes, loading: classesLoading } = useFamilyMembersData(memberIds, 'class');
  // Write operations still use the identity API via useFamilyData
  const { insert: insertClass, update: updateClass, remove: removeClass } = useFamilyData('class');

  // Filter contacts to get schools
  const schools = allContacts.filter((c) => c.data?.data?.category?.toLowerCase() === 'school');
  const schoolsLoading = contactsLoading;

  // School CRUD operations work on contacts
  const insertSchool = async (data: any, familyId?: string) => insertContact({ ...data, category: 'school' }, familyId);
  const updateSchool = async (id: string, data: any) => updateContact(id, { ...data, category: 'school' });
  const removeSchool = removeContact;

  // UI states
  const [expandedSchool, setExpandedSchool] = useState<string | null>(null);

  // Dialog states
  const [schoolDialogOpen, setSchoolDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [defaultSchoolId, setDefaultSchoolId] = useState<string | null>(null);

  const loading = schoolsLoading || classesLoading;

  // Handlers
  const handleSaveSchool = async (data: any, familyIds: string[]) => {
    try {
      if (selectedSchool?.id) {
        await updateSchool(selectedSchool.id, data);
        enqueueSnackbar('School updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertSchool(data, familyId);
        }
        enqueueSnackbar('School added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save school', { variant: 'error' });
      throw err;
    }
  };

  const handleSaveClass = async (data: any, familyIds: string[]) => {
    try {
      if (selectedClass?.id) {
        await updateClass(selectedClass.id, data);
        enqueueSnackbar('Class updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertClass({ ...data, schoolId: data.schoolId || defaultSchoolId }, familyId);
        }
        enqueueSnackbar('Class added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save class', { variant: 'error' });
      throw err;
    }
  };

  const getClassesForSchool = (schoolId: string) => {
    return classes.filter((c: any) => c.data?.data?.schoolId === schoolId);
  };

  const handleAddClass = (schoolId: string) => {
    setDefaultSchoolId(schoolId);
    setSelectedClass(null);
    setClassDialogOpen(true);
  };

  const handleToggleAssignment = async (cls: any, assignmentId: string) => {
    const classData = (cls.data?.data || {}) as any;
    const assignments: ClassAssignment[] = classData.assignments || [];
    const updatedAssignments = assignments.map((a: ClassAssignment) =>
      a.id === assignmentId ? { ...a, completed: !a.completed } : a
    );
    try {
      await updateClass(cls.id, { ...classData, assignments: updatedAssignments });
    } catch (err) {
      enqueueSnackbar('Failed to update assignment', { variant: 'error' });
    }
  };

  const formatDueDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const isOverdue = (dateStr?: string): boolean => {
    if (!dateStr) return false;
    return new Date(dateStr + 'T23:59:59') < new Date();
  };

  const renderAssignments = (cls: any) => {
    const classData = (cls.data?.data || {}) as any;
    const assignments: ClassAssignment[] = classData.assignments || [];
    const incomplete = assignments.filter((a) => !a.completed);
    if (incomplete.length === 0) return null;

    return (
      <Box sx={{ pl: 5, pr: 1, pb: 0.5 }}>
        {incomplete.map((assignment) => {
          const typeInfo = getAssignmentTypeInfo(assignment.type);
          const overdue = isOverdue(assignment.dueDate);
          return (
            <Box
              key={assignment.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
              }}
            >
              <Checkbox
                size="small"
                checked={false}
                onChange={() => handleToggleAssignment(cls, assignment.id)}
                sx={{ p: 0.25 }}
              />
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: typeInfo.color + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: typeInfo.color,
                '& .MuiSvgIcon-root': { fontSize: 14 },
              }}>
                {typeInfo.icon}
              </Box>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {assignment.name}
              </Typography>
              {assignment.dueDate && (
                <Typography
                  variant="caption"
                  sx={{
                    color: overdue ? 'error.main' : 'text.secondary',
                    fontWeight: overdue ? 600 : 400,
                    flexShrink: 0,
                    ml: 1,
                  }}
                >
                  {formatDueDate(assignment.dueDate)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading school data..." />;
  }

  return (
    <Box>
      <PageHeader
        title="School"
        subtitle="Manage schools, classes, and assignments"
        actionLabel="Add School"
        actionIcon={AddIcon}
        onAction={() => {
          setSelectedSchool(null);
          setSchoolDialogOpen(true);
        }}
      />

      <Grid container spacing={3}>
        {/* Stats */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Schools"
            icon={SchoolIcon}
            iconColor={colors.school}
            value={schools.length}
            onClick={() => {
              setSelectedSchool(null);
              setSchoolDialogOpen(true);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Classes"
            icon={ClassIcon}
            iconColor={colors.school}
            value={classes.length}
            onClick={() => {
              setDefaultSchoolId(null);
              setSelectedClass(null);
              setClassDialogOpen(true);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Open Assignments"
            icon={AssignmentIcon}
            iconColor={colors.school}
            value={classes.reduce((total: number, cls: any) => {
              const data = (cls.data?.data || {}) as any;
              return total + ((data.assignments || []) as ClassAssignment[]).filter((a) => !a.completed).length;
            }, 0)}
          />
        </Grid>

        {/* Schools List */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Schools</Typography>
              </Box>

              {schools.length === 0 ? (
                <EmptyState
                  icon={SchoolIcon}
                  title="No schools yet"
                  description="Add a school to start tracking classes and assignments"
                  actionLabel="Add School"
                  onAction={() => {
                    setSelectedSchool(null);
                    setSchoolDialogOpen(true);
                  }}
                />
              ) : (
                <List disablePadding>
                  {schools.map((school) => {
                    const schoolData = (school.data?.data || {}) as any;
                    const schoolClasses = getClassesForSchool(school.id);
                    const isExpanded = expandedSchool === school.id;

                    return (
                      <Box key={school.id}>
                        <ListItemButton
                          sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            mb: 1,
                            border: 1,
                            borderColor: 'divider',
                          }}
                          onClick={() => {
                            setSelectedSchool(school);
                            setSchoolDialogOpen(true);
                          }}
                        >
                          <ListItemIcon>
                            <SchoolIcon sx={{ color: colors.school }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={schoolData.name || 'Unnamed School'}
                            secondary={[schoolData.type, schoolData.city, `${schoolClasses.length} classes`].filter(Boolean).join(' · ')}
                          />
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSchool(isExpanded ? null : school.id);
                            }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </ListItemButton>

                        <Collapse in={isExpanded}>
                          <Box sx={{ pl: 4, pb: 2 }}>
                            {schoolClasses.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No classes added yet
                              </Typography>
                            ) : (
                              <List dense disablePadding>
                                {schoolClasses.map((cls: any) => {
                                  const classData = (cls.data?.data || {}) as any;
                                  const assignmentCount = (classData.assignments || []).filter((a: ClassAssignment) => !a.completed).length;
                                  return (
                                    <Box key={cls.id}>
                                      <ListItemButton
                                        sx={{ pl: 0, borderRadius: 1 }}
                                        onClick={() => {
                                          setSelectedClass(cls);
                                          setClassDialogOpen(true);
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                          <ClassIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {classData.name || 'Unnamed Class'}
                                              {classData.subject && <Chip label={classData.subject} size="small" variant="outlined" />}
                                              {assignmentCount > 0 && (
                                                <Chip
                                                  icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                                                  label={assignmentCount}
                                                  size="small"
                                                  color="warning"
                                                  variant="outlined"
                                                />
                                              )}
                                            </Box>
                                          }
                                          secondary={[classData.teacher && `Teacher: ${classData.teacher}`, classData.room && `Room ${classData.room}`, classData.days?.join('/'), classData.startTime && `${classData.startTime}${classData.endTime ? `-${classData.endTime}` : ''}`].filter(Boolean).join(' · ')}
                                        />
                                      </ListItemButton>
                                      {renderAssignments(cls)}
                                    </Box>
                                  );
                                })}
                              </List>
                            )}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              sx={{ mt: 1 }}
                              onClick={() => handleAddClass(school.id)}
                            >
                              Add Class
                            </Button>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* All Classes */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">All Classes</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => { setDefaultSchoolId(null); setSelectedClass(null); setClassDialogOpen(true); }}>
                  Add Class
                </Button>
              </Box>

              {classes.length === 0 ? (
                <EmptyState
                  icon={ClassIcon}
                  title="No classes yet"
                  description="Add a class to start tracking schedules and assignments"
                  actionLabel="Add Class"
                  onAction={() => { setDefaultSchoolId(null); setSelectedClass(null); setClassDialogOpen(true); }}
                />
              ) : (
                <List disablePadding>
                  {classes.map((cls: any) => {
                    const classData = (cls.data?.data || {}) as any;
                    const schoolName = schools.find((s) => s.id === classData.schoolId)?.data?.data?.name;
                    const assignmentCount = (classData.assignments || []).filter((a: ClassAssignment) => !a.completed).length;
                    return (
                      <Box key={cls.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                        <ListItemButton
                          sx={{ borderRadius: 1 }}
                          onClick={() => { setSelectedClass(cls); setClassDialogOpen(true); }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ClassIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {classData.name || 'Unnamed Class'}
                                {classData.subject && <Chip label={classData.subject} size="small" variant="outlined" />}
                                {assignmentCount > 0 && (
                                  <Chip
                                    icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                                    label={assignmentCount}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={[schoolName, classData.teacher && `Teacher: ${classData.teacher}`, classData.room && `Room ${classData.room}`, classData.days?.join('/'), classData.startTime && `${classData.startTime}${classData.endTime ? `-${classData.endTime}` : ''}`].filter(Boolean).join(' · ')}
                          />
                        </ListItemButton>
                        {renderAssignments(cls)}
                      </Box>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <SchoolFormDialog
        open={schoolDialogOpen}
        onClose={() => { setSchoolDialogOpen(false); setSelectedSchool(null); }}
        onSave={handleSaveSchool}
        onDelete={selectedSchool ? async () => {
          try { await removeSchool(selectedSchool.id); enqueueSnackbar('School deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete school', { variant: 'error' }); throw err; }
        } : undefined}
        school={selectedSchool}
      />

      <ClassFormDialog
        open={classDialogOpen}
        onClose={() => { setClassDialogOpen(false); setSelectedClass(null); setDefaultSchoolId(null); }}
        onSave={handleSaveClass}
        onDelete={selectedClass ? async () => {
          try { await removeClass(selectedClass.id); enqueueSnackbar('Class deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete class', { variant: 'error' }); throw err; }
        } : undefined}
        classItem={selectedClass}
        schools={schools}
      />
    </Box>
  );
};

export default SchoolPage;
